import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";

interface PlaylistDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (playlist: { name: string; description?: string }) => void | Promise<void>;
  initialName?: string;
  initialDescription?: string;
  mode?: "create" | "edit";
}

const PlaylistDialog: React.FC<PlaylistDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialName = "",
  initialDescription = "",
  mode = "create",
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset form when dialog opens/closes or initial values change
  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setError("");
    }
  }, [open, initialName, initialDescription]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Playlist name is required");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await onSuccess({ name: name.trim(), description: description.trim() || undefined });
      handleClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to save playlist. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "create" ? "Create Playlist" : "Edit Playlist"}</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="playlist-name"
            label="Playlist Name"
            name="name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            error={!name.trim() && name.length > 0}
            helperText={!name.trim() && name.length > 0 ? "Playlist name cannot be empty" : ""}
          />
          <TextField
            margin="dense"
            fullWidth
            id="playlist-description"
            label="Description (optional)"
            name="description"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
          />
          
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button 
              onClick={handleClose} 
              fullWidth 
              variant="outlined"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              fullWidth 
              variant="contained" 
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                mode === "create" ? "Create" : "Save"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistDialog;
